import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { Observable, of } from "rxjs";
import { ProblemType } from "../../utilities/interfaces/Helpdesk_Interfaces/problem-type.interface";
import { Helpdesk } from "../../utilities/interfaces/Helpdesk_Interfaces/helpdesk.interface";
import { map, catchError } from "rxjs/operators";
import { Portfolio } from "../../utilities/interfaces/CMS_Interfaces/portfolio_interfaces/portfolio.interface";
import { Type } from "../../utilities/interfaces/CMS_Interfaces/portfolio_interfaces/type.interface";
import { Team } from "../../utilities/interfaces/CMS_Interfaces/team_interfaces/team.interface";
import { Testimonial } from "../../utilities/interfaces/CMS_Interfaces/testimonial_interfaces/testimonial.interface";
import { Skill } from "../../utilities/interfaces/CMS_Interfaces/skill_interfaces/skill.interface";
import { Gallery } from "../../utilities/interfaces/Document_Interfaces/gallery_interfaces/gallery.interface";
import { LatestNews } from "../../utilities/interfaces/CMS_Interfaces/latest_news_interfaces/latestnews.interface";
import { Notice } from "../../utilities/interfaces/CMS_Interfaces/notice_interfaces/notice.interface";
import { Contact } from "../../utilities/interfaces/CMS_Interfaces/feedback_interfaces/contact.interface";
import { Product } from "../../utilities/interfaces/Inventory_Interfaces/product.interface";
import { Order } from "../../utilities/interfaces/Inventory_Interfaces/order.interface";
import { Job } from "../../utilities/interfaces/CMS_Interfaces/job_interfaces/jobs.interface";
import { Popup } from "../../utilities/interfaces/Document_Interfaces/popup_interfaces/popup.interface";
import { Slider } from "../../utilities/interfaces/Document_Interfaces/slider_interfaces/slider.interface";
import { Blog } from "../../utilities/interfaces/Blog_Interfaces/blog.interface";
import { environment } from "../../../environments/environment";
import { PricingPlan } from "../../utilities/interfaces/CMS_Interfaces/pricing_plan_interfaces/pricing-plan.interface";

@Injectable({ providedIn: "root" })
export class LandingService {
  private baseUrl = `${environment.backendUrl}`;

  constructor(private http: HttpClient) {}

  getPortfolios(): Observable<Portfolio[]> {
    const portfoliosUrl = `${this.baseUrl}/api/public/portfolios?_=${Date.now()}`;
    return this.http
      .get<Portfolio[]>(portfoliosUrl)
      .pipe(
        map((portfolios) => this.processPortfolioImages(portfolios)),
        catchError((error) => { console.error('Error fetching portfolios:', error); return of([]); })
      );
  }

  getTypes(): Observable<Type[]> {
    const typesUrl = `${this.baseUrl}/api/public/types?_=${Date.now()}`;
    return this.http.get<Type[]>(typesUrl).pipe(
      catchError((error) => { console.error('Error fetching types:', error); return of([]); })
    );
  }

  getTeams(): Observable<Team[]> {
    const teamsUrl = `${this.baseUrl}/api/public/teams?_=${Date.now()}`;
    return this.http
      .get<Team[]>(teamsUrl)
      .pipe(
        map((teams) => this.processTeamImages(teams)),
        catchError((error) => { console.error('Error fetching teams:', error); return of([]); })
      );
  }

  getTestimonials(): Observable<Testimonial[]> {
    const testimonialsUrl = `${this.baseUrl}/api/public/testimonials?_=${Date.now()}`;
    return this.http.get<Testimonial[]>(testimonialsUrl).pipe(
      map((testimonials) => {
        if (!testimonials || !Array.isArray(testimonials)) {
          console.warn("Invalid testimonials data received:", testimonials);
          return [];
        }
        return this.processTestimonialImages(testimonials);
      }),
      catchError((error) => { console.error('Error fetching testimonials:', error); return of([]); })
    );
  }

  getSkills(): Observable<Skill[]> {
    const skillsUrl = `${this.baseUrl}/api/public/skills?_=${Date.now()}`;
    return this.http.get<Skill[]>(skillsUrl).pipe(
      catchError((error) => { console.error('Error fetching skills:', error); return of([]); })
    );
  }

  getFaqs(): Observable<any[]> {
    const faqsUrl = `${this.baseUrl}/api/public/faqs?_=${Date.now()}`;
    return this.http.get<any[]>(faqsUrl).pipe(
      map((categories) => {
        const sortedCategories = [...categories].sort(
          (a, b) => (a.weight || 0) - (b.weight || 0),
        );
        return sortedCategories.map((category) => {
          if (category.faqs && Array.isArray(category.faqs)) {
            category.faqs = [...category.faqs].sort(
              (a, b) => (a.weight || 0) - (b.weight || 0),
            );
          }
          return category;
        });
      }),
      catchError((error) => { console.error('Error fetching FAQs:', error); return of([]); })
    );
  }

  getPageBySlug(slug: string): Observable<any> {
    const url = `${this.baseUrl}/api/public/pages/${slug}?_=${Date.now()}`;
    return this.http.get<any>(url).pipe(
      map((page) => {
        if (page.image) {
          page.image = `${this.baseUrl}${page.image}`;
        }
        if (page.attachFile) {
          page.attachFile = `${this.baseUrl}${page.attachFile}`;
        }
        return page;
      }),
    );
  }

  getGalleries(): Observable<Gallery[]> {
    const galleriesUrl = `${this.baseUrl}/api/public/galleries?_=${Date.now()}`;
    return this.http
      .get<Gallery[]>(galleriesUrl)
      .pipe(
        map((galleries) => this.processGalleryImages(galleries)),
        catchError((error) => { console.error('Error fetching galleries:', error); return of([]); })
      );
  }

  getNews(): Observable<LatestNews[]> {
    const newsUrl = `${this.baseUrl}/api/public/news?_=${Date.now()}`;
    return this.http
      .get<LatestNews[]>(newsUrl)
      .pipe(
        map((newsItems) => this.processNewsImages(newsItems)),
        catchError((error) => { console.error('Error fetching news:', error); return of([]); })
      );
  }

  getNotices(): Observable<Notice[]> {
    const noticesUrl = `${this.baseUrl}/api/public/notices?_=${Date.now()}`;
    return this.http
      .get<Notice[]>(noticesUrl)
      .pipe(
        map((notices) => this.processNoticeFiles(notices)),
        catchError((error) => { console.error('Error fetching notices:', error); return of([]); })
      );
  }

  getPricingPlans(): Observable<PricingPlan[]> {
    const url = `${this.baseUrl}/api/public/pricing-plans?_=${Date.now()}`;
    return this.http.get<PricingPlan[]>(url).pipe(
      catchError((error) => { console.error('Error fetching pricing plans:', error); return of([]); })
    );
  }

  getCaptcha(): Observable<any> {
    const captchaUrl = `${this.baseUrl}/api/public/captcha?_=${Date.now()}`;
    return this.http.get<any>(captchaUrl);
  }

  createContact(
    contactData: Contact & { captchaText: string; captchaHash: string },
  ): Observable<Contact> {
    const contactsUrl = `${this.baseUrl}/api/public/contacts`;
    return this.http.post<Contact>(contactsUrl, contactData);
  }

  createOrder(
    orderData: Order & { captchaText: string; captchaHash: string },
  ): Observable<Order> {
    const ordersUrl = `${this.baseUrl}/api/public/orders`;
    return this.http.post<Order>(ordersUrl, orderData);
  }

  createApplication(applicationData: FormData): Observable<any> {
    const applicationsUrl = `${this.baseUrl}/api/public/applications`;
    return this.http.post<any>(applicationsUrl, applicationData);
  }

  createHelpdesk(helpdeskData: FormData): Observable<Helpdesk> {
    const url = `${this.baseUrl}/api/public/helpdesks`;
    return this.http.post<Helpdesk>(url, helpdeskData);
  }

  getProducts(): Observable<Product[]> {
    const productsUrl = `${this.baseUrl}/api/public/products?_=${Date.now()}`;
    return this.http
      .get<Product[]>(productsUrl)
      .pipe(
        map((products) => this.processProductImages(products)),
        catchError((error) => { console.error('Error fetching products:', error); return of([]); })
      );
  }

  getProductCategories(): Observable<any[]> {
    const categoriesUrl = `${this.baseUrl}/api/public/products/categories?_=${Date.now()}`;
    return this.http
      .get<any[]>(categoriesUrl)
      .pipe(
        map((categories) => this.processCategoryImages(categories)),
        catchError((error) => { console.error('Error fetching product categories:', error); return of([]); })
      );
  }

  getProduct(slug: string | number): Observable<Product> {
    const url = `${this.baseUrl}/api/public/products/${slug}?_=${Date.now()}`;
    return this.http
      .get<Product>(url)
      .pipe(map((product) => this.processProductImages([product])[0]));
  }

  getJobs(): Observable<Job[]> {
    const jobsUrl = `${this.baseUrl}/api/public/jobs?_=${Date.now()}`;
    return this.http.get<Job[]>(jobsUrl).pipe(
      catchError((error) => { console.error('Error fetching jobs:', error); return of([]); })
    );
  }

  getJob(id: number): Observable<Job> {
    const url = `${this.baseUrl}/api/public/jobs/get-by-id`;
    return this.http.post<Job>(url, { id });
  }

  getProblemTypes(): Observable<ProblemType[]> {
    const url = `${this.baseUrl}/api/public/problem-types?_=${Date.now()}`;
    return this.http.get<ProblemType[]>(url).pipe(
      catchError((error) => { console.error('Error fetching problem types:', error); return of([]); })
    );
  }

  getPopups(): Observable<Popup[]> {
    const url = `${this.baseUrl}/api/public/popups?_=${Date.now()}`;
    return this.http.get<Popup[]>(url).pipe(
      map((popups) =>
        popups.map((popup) => ({
          ...popup,
          image: popup.image
            ? `${this.baseUrl}/${popup.image.replace(/^\//, "")}`
            : "",
        })),
      ),
      catchError((error) => { console.error('Error fetching popups:', error); return of([]); })
    );
  }

  getSliders(): Observable<Slider[]> {
    const slidersUrl = `${this.baseUrl}/api/public/sliders?_=${Date.now()}`;
    return this.http
      .get<Slider[]>(slidersUrl)
      .pipe(
        map((sliders) => this.processSliderImages(sliders)),
        catchError((error) => { console.error('Error fetching sliders:', error); return of([]); })
      );
  }

  getBlogs(userEmail?: string): Observable<Blog[]> {
    let params = new HttpParams().set("_", Date.now().toString());
    if (userEmail) {
      params = params.set("userEmail", userEmail);
    }
    const url = `${this.baseUrl}/api/public/blogs`;
    return this.http
      .get<Blog[]>(url, { params })
      .pipe(
        map((blogs) => this.processBlogImages(blogs)),
        catchError((error) => { console.error('Error fetching blogs:', error); return of([]); })
      );
  }

  getBlog(id: number, userEmail?: string): Observable<Blog> {
    let params = new HttpParams().set("_", Date.now().toString());
    if (userEmail) {
      params = params.set("userEmail", userEmail);
    }
    const url = `${this.baseUrl}/api/public/blogs/get-by-id`;
    return this.http
      .post<Blog>(url, { id, userEmail })
      .pipe(map((b) => this.processBlogImages([b])[0]));
  }

  private processBlogImages(blogs: Blog[]): Blog[] {
    if (!Array.isArray(blogs)) return [];

    return blogs.map((b) => {
      if (b.image && !b.image.startsWith("http")) {
        b.image = `${this.baseUrl}/${b.image.replace(/^\/+/, "")}`;
      }
      if (b.attachment && !b.attachment.startsWith("http")) {
        b.attachment = `${this.baseUrl}/${b.attachment.replace(/^\/+/, "")}`;
      }
      return b;
    });
  }

  getBlogFileUrl(filename: string): string {
    if (!filename) return "";
    if (filename.startsWith("http")) return filename;
    return `${this.baseUrl}/api/public/blogs/files/${filename}`;
  }

  private processSliderImages(sliders: Slider[]): Slider[] {
    if (!sliders || !Array.isArray(sliders)) {
      console.warn("Invalid sliders data:", sliders);
      return [];
    }
    return sliders.map((slider) => {
      if (slider.image && !slider.image.startsWith("http")) {
        return {
          ...slider,
          image: `${this.baseUrl}/${slider.image.replace(/^\//, "")}`,
        };
      }
      return slider;
    });
  }

  getSliderImageUrl(imagePath: string): string {
    if (!imagePath) return "assets/placeholder.png";
    if (imagePath.startsWith("http")) {
      return imagePath;
    }
    return `${this.baseUrl}/${imagePath.replace(/^\//, "")}`;
  }

  private processPortfolioImages(portfolios: Portfolio[]): Portfolio[] {
    if (!portfolios || !Array.isArray(portfolios)) {
      console.warn("Invalid portfolios data:", portfolios);
      return [];
    }
    return portfolios.map((portfolio) => {
      if (portfolio.image && !portfolio.image.startsWith("http")) {
        return {
          ...portfolio,
          image: `${this.baseUrl}/${portfolio.image.replace(/^\//, "")}`,
        };
      }
      return portfolio;
    });
  }

  private processTeamImages(teams: Team[]): Team[] {
    if (!teams || !Array.isArray(teams)) {
      console.warn("Invalid teams data:", teams);
      return [];
    }
    return teams.map((team) => {
      if (team.image && !team.image.startsWith("http")) {
        return {
          ...team,
          image: `${this.baseUrl}/${team.image.replace(/^\//, "")}`,
        };
      }
      return team;
    });
  }

  private processTestimonialImages(testimonials: Testimonial[]): Testimonial[] {
    if (!testimonials || !Array.isArray(testimonials)) {
      console.warn("Invalid testimonials data:", testimonials);
      return [];
    }
    return testimonials.map((testimonial) => {
      if (testimonial.image && !testimonial.image.startsWith("http")) {
        return {
          ...testimonial,
          image: `${this.baseUrl}/${testimonial.image.replace(/^\//, "")}`,
        };
      }
      return testimonial;
    });
  }

  private processGalleryImages(galleries: Gallery[]): Gallery[] {
    if (!galleries || !Array.isArray(galleries)) {
      console.warn("Invalid galleries data:", galleries);
      return [];
    }
    return galleries.map((gallery) => {
      if (gallery.image && !gallery.image.startsWith("http")) {
        return {
          ...gallery,
          image: `${this.baseUrl}/${gallery.image.replace(/^\//, "")}`,
        };
      }
      return gallery;
    });
  }

  private processNewsImages(newsItems: LatestNews[]): LatestNews[] {
    if (!newsItems || !Array.isArray(newsItems)) {
      console.warn("Invalid news data:", newsItems);
      return [];
    }
    return newsItems.map((news) => {
      if (news.image && !news.image.startsWith("http")) {
        return {
          ...news,
          image: `${this.baseUrl}/${news.image.replace(/^\//, "")}`,
        };
      }
      return news;
    });
  }

  private processNoticeFiles(notices: Notice[]): Notice[] {
    if (!notices || !Array.isArray(notices)) {
      console.warn("Invalid notices data:", notices);
      return [];
    }
    return notices.map((notice) => {
      if (notice.attachFile && !notice.attachFile.startsWith("http")) {
        return {
          ...notice,
          attachFile: `${this.baseUrl}/${notice.attachFile.replace(/^\//, "")}`,
        };
      }
      return notice;
    });
  }

  private processProductImages(products: Product[]): Product[] {
    if (!products || !Array.isArray(products)) {
      console.warn("Invalid products data:", products);
      return [];
    }
    return products.map((product) => {
      if (product.image && !product.image.startsWith("http")) {
        product.image = `${this.baseUrl}/${product.image.replace(/^\//, "")}`;
      }
      if (
        product.category &&
        product.category.image &&
        !product.category.image.startsWith("http")
      ) {
        product.category.image = `${this.baseUrl}/${product.category.image.replace(/^\//, "")}`;
      }
      if (
        product.company &&
        product.company.image &&
        !product.company.image.startsWith("http")
      ) {
        product.company.image = `${this.baseUrl}/${product.company.image.replace(/^\//, "")}`;
      }
      return product;
    });
  }

  private processCategoryImages(categories: any[]): any[] {
    if (!categories || !Array.isArray(categories)) {
      console.warn("Invalid categories data:", categories);
      return [];
    }
    return categories.map((category) => {
      if (category.image && !category.image.startsWith("http")) {
        return {
          ...category,
          image: `${this.baseUrl}/${category.image.replace(/^\//, "")}`,
        };
      }
      return category;
    });
  }

  getPortfolioImageUrl(imagePath: string): string {
    if (!imagePath) return "assets/placeholder.png";
    if (imagePath.startsWith("http")) {
      return imagePath;
    }
    return `${this.baseUrl}/${imagePath.replace(/^\//, "")}`;
  }

  getTeamImageUrl(imagePath: string): string {
    if (!imagePath) return "assets/placeholder.png";
    if (imagePath.startsWith("http")) {
      return imagePath;
    }
    return `${this.baseUrl}/${imagePath.replace(/^\//, "")}`;
  }

  getTestimonialImageUrl(imagePath: string): string {
    if (!imagePath) return "assets/placeholder.png";
    if (imagePath.startsWith("http")) {
      return imagePath;
    }
    return `${this.baseUrl}/${imagePath.replace(/^\//, "")}`;
  }

  getGalleryImageUrl(imagePath: string): string {
    if (!imagePath) return "assets/placeholder.png";
    if (imagePath.startsWith("http")) {
      return imagePath;
    }
    return `${this.baseUrl}/${imagePath.replace(/^\//, "")}`;
  }

  getNewsImageUrl(imagePath: string): string {
    if (!imagePath) return "assets/placeholder.png";
    if (imagePath.startsWith("http")) {
      return imagePath;
    }
    return `${this.baseUrl}/${imagePath.replace(/^\//, "")}`;
  }

  getNoticeFileUrl(filePath: string): string {
    if (!filePath) return "";
    if (filePath.startsWith("http")) {
      return filePath;
    }
    return `${this.baseUrl}/${filePath.replace(/^\//, "")}`;
  }
}
